// ControlButtons.js
import React, { useEffect, useState, useRef } from "react";
import TooltipWrapper from "@/components/TooltipWrapper";
import { Button } from "@/components/ui/button";
import { useTour } from "@reactour/tour";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ReloadIcon,
  PlayIcon,
  PauseIcon,
  ResetIcon,
  SpeakerLoudIcon,
} from "@radix-ui/react-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStop } from "@fortawesome/free-solid-svg-icons";

import useStore from "@/store";
import "./ControlButtons.css";


const ControlButtons = ({
  mode,
  onPrev,
  onNext,
  endOfCollectionReached,
  onReset,
  onStartStopRecording,
  onStartStopListening,
  isRecording,
  isListening,
  isGeneratingAudio,
  isPredicting,
  processing,
  handleReplay,
  isVideoPlaying,
  setIsVideoPlaying,
}) => {
  const { currentCollection } = useStore().collections;
  const {
    user,
    onboardingEnabled,
    onboardingStages,
    setOnboardingStage,
  } = useStore().user;
  const { isOnboarding } = useStore().onboarding;
  const {
    wordPracticeDialogOpened,
    triggerPauseVideo,
    disableShortcuts,
  } = useStore().learningSessionConfig;

  const { setIsOpen } = useTour();

  // Start the onboarding tour if needed
  useEffect(() => {
    setIsOpen(isOnboarding);
  }, [isOnboarding, setIsOpen]);

  // SpeechCraft is free: every mode is available to everyone.
  const handleStartStop = () => {
    triggerPauseVideo();
    if (processing || isPredicting) return;
    onStartStopRecording();
  };

  // Keyboard shortcuts (space / K / L / R / arrows)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (wordPracticeDialogOpened || disableShortcuts) return;
      if (e.metaKey || e.shiftKey || e.altKey || e.ctrlKey) return;

      switch (e.key) {
        case " ":
          if (!e.repeat) {
            triggerPauseVideo();
            e.preventDefault();
            handleStartStop();
          }
          break;
        case "k":
        case "K":
          e.preventDefault();
          triggerPauseVideo();
          if (isGeneratingAudio) {
            return;
          }
          onStartStopListening(0.6);
          break;
        case "l":
        case "L":
          e.preventDefault();
          triggerPauseVideo();
          if (isGeneratingAudio) {
            return;
          }
          onStartStopListening(1);
          break;
        case "r":
        case "R":
          e.preventDefault();
          onReset();
          break;
        case "ArrowRight":
          e.preventDefault();
          onNext();
          break;
        case "ArrowLeft":
          e.preventDefault();
          onPrev();
          break;
        default:
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    wordPracticeDialogOpened,
    disableShortcuts,
    triggerPauseVideo,
    onStartStopListening,
    onReset,
    onNext,
    onPrev,
    handleStartStop,
    isGeneratingAudio,
  ]);

  const onReplayClick = () => {
    handleReplay();
  };

  return (
    <div className="flex items-center">
      {/* ← Prev */}
      <TooltipWrapper tooltipText="Go to previous content">
        <Button
          variant="outline"
          size="icon"
          onClick={onPrev}
          className="md:mr-6 mr-3"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </Button>
      </TooltipWrapper>

      {/* Reset / Listen controls */}
      {currentCollection.content_type === "youtube" ? (
        <>
          <TooltipWrapper tooltipText="Listen to the content">
            <Button
              variant="outline"
              className="px-4 rounded-r-none"
              disabled={isGeneratingAudio}
              onClick={() => {
                triggerPauseVideo();
                onStartStopListening(1);
              }}
            >
              {isGeneratingAudio ? (
                <svg
                  className="h-4 w-4 animate-spin text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : isListening ? (
                <>
                  <PauseIcon className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Pause</span>
                </>
              ) : (
                <>
                  <SpeakerLoudIcon className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Listen</span>
                </>
              )}
            </Button>
          </TooltipWrapper>
          <TooltipWrapper tooltipText="Reset the speaking session">
            <Button
              className="px-4 rounded-l-none"
              variant="outline"
              onClick={onReset}
            >
              <ResetIcon className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Reset</span>
            </Button>
          </TooltipWrapper>
        </>
      ) : (
        <TooltipWrapper tooltipText="Reset the speaking session">
          <Button className="px-4" variant="outline" onClick={onReset}>
            <ResetIcon className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">Reset</span>
          </Button>
        </TooltipWrapper>
      )}

      {/* ● Mic / recording button */}
      <TooltipWrapper
        tooltipText="Speak"
        onboarding={
          onboardingEnabled && !onboardingStages.speakButtonClicked
        }
        onboardingMessage="Please click this button to start speaking the text"
      >
        <button
          className={`mic-control relative flex items-center justify-center transition duration-300 ease-in-out md:mx-6 mx-3 ${
            isRecording || processing || isPredicting
              ? "mic-control--recording"
              : "mic-control--idle"
          } ${isPredicting ? "cursor-not-allowed" : "cursor-pointer"}`}
          disabled={processing || isPredicting}
          onClick={() => {
            if (
              onboardingEnabled &&
              !onboardingStages.speakButtonClicked
            ) {
              setOnboardingStage("speakButtonClicked", true);
            }
            handleStartStop();
          }}
        >
          {isPredicting ? (
            <div className="flex justify-center items-center w-full h-full">
              <svg
                className="animate-spin h-8 w-8 text-fg"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : isRecording ? (
            <FontAwesomeIcon icon={faStop} className="icon text-fg" />
          ) : (
            <FontAwesomeIcon icon={faMicrophone} className="icon" />
          )}
        </button>
      </TooltipWrapper>

      {/* ▶️ / 🔄 / ▶️ Next */}
      {currentCollection.content_type === "youtube" ? (
        <>
          <TooltipWrapper tooltipText="Toggle video playback">
            <Button
              variant="outline"
              className="px-4 rounded-r-none"
              onClick={() => setIsVideoPlaying(!isVideoPlaying)}
            >
              {isVideoPlaying ? (
                <>
                  <PauseIcon className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Pause</span>
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4" />
                  <span className="ml-1 hidden sm:inline">Play</span>
                </>
              )}
            </Button>
          </TooltipWrapper>
          <TooltipWrapper tooltipText="Replay video segment">
            <Button
              variant="outline"
              className="px-4 rounded-l-none"
              onClick={onReplayClick}
            >
              <ReloadIcon className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Replay</span>
            </Button>
          </TooltipWrapper>
        </>
      ) : (
        <TooltipWrapper tooltipText="Replay audio segment">
          <Button
            variant="outline"
            className="px-4"
            disabled={isGeneratingAudio}
            onClick={() => onStartStopListening(1)}
          >
            {isGeneratingAudio ? (
              <svg
                className="h-4 w-4 animate-spin text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : isListening ? (
              <>
                <PauseIcon className="h-4 w-4" />
                <span className="ml-1 hidden sm:inline">Pause</span>
              </>
            ) : (
              <>
                <SpeakerLoudIcon className="h-4 w-4" />
                <span className="ml-1 hidden sm:inline">Listen</span>
              </>
            )}
          </Button>
        </TooltipWrapper>
      )}

      <TooltipWrapper tooltipText="Go to next content">
        <Button
          variant="outline"
          size="icon"
          onClick={onNext}
          disabled={endOfCollectionReached}
          className="md:ml-6 ml-3"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </Button>
      </TooltipWrapper>

    </div>
  );
};

export default ControlButtons;
